import React from 'react';
import { generatePath, Navigate, RouteObject } from 'react-router-dom';
import { AuthenticatedLayout } from '../components/AuthenticatedLayout';
import { PageLayout } from '../components/PageLayout';
import { ScrollToTop } from '../components/ScrollToTop';
import OrdersPage from '../pages/OrdersPage/OrdersPage';
import OrderDetailPage from '../pages/OrderDetailPage';
import ShipmentPage from '../pages/ShipmentPage/ShipmentPage';
import PackingListPage from '../pages/PackingListPage';
import { TermsAndConditionsPage } from '../pages/TermsAndConditionsPage/TermsAndConditionsPage';

type PageName =
  | 'HomePage'
  | 'LoginPage'
  | 'OrdersPage'
  | 'ShipmentPage'
  | 'OrderDetailPage'
  | 'ShippingAuthPage'
  | 'TermsAndConditionsPage'
  | 'PackingListPage';

// Root layout that includes ScrollToTop and PageLayout
const RootLayout: React.FC = () => (
  <>
    <ScrollToTop />
    <PageLayout />
  </>
);

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Public routes
      {
        path: 'terms-and-conditions',
        element: <TermsAndConditionsPage />
      },
      // Protected routes (wrapped in AuthenticatedLayout)
      {
        element: <AuthenticatedLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/orders" replace />
          },
          {
            path: 'login',
            element: <Navigate to="/orders" replace />
          },
          {
            path: 'orders',
            element: <OrdersPage />
          },
          {
            path: 'order/:poNumber',
            element: <OrderDetailPage />
          },
          {
            path: 'order/:poNumber/shipping-auth',
            element: <ShipmentPage />
          },
          {
            path: 'packing-list/:asnsNumber',
            element: <PackingListPage />
          }
        ]
      }
    ]
  }
];

// Keep hrefFor for backward compatibility
const routePaths: Record<PageName, string> = {
  HomePage: '/',
  LoginPage: '/login',
  OrdersPage: '/orders',
  OrderDetailPage: '/order/:poNumber',
  ShipmentPage: '/order/:poNumber/shipping-auth',
  ShippingAuthPage: '/order/:poNumber/shipping-auth',
  TermsAndConditionsPage: '/terms-and-conditions',
  PackingListPage: '/packing-list/:asnsNumber'
};

export type Params = { [param: string]: string };
export type HrefFor = Record<PageName, (params?: Record<string, string>) => string>;

export const hrefFor: HrefFor = Object.entries(routePaths).reduce(
  (acc, [key, path]) => ({
    ...acc,
    [key]: (params = {}) => generatePath(path, params)
  }),
  {} as HrefFor
);
